import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { TOOLBAR_CONTROL } from '@/components/ui/pageChrome';
import { TYPE, UI_MUTED } from '@/lib/typography';

export interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  allLabel: string;
  options: MultiSelectOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  emptyLabel?: string;
  /** Extra mutually exclusive "all" rows. The default all-checkbox stays for callers that omit this. */
  headerOptions?: Array<{
    id: string;
    label: string;
    active: boolean;
    onSelect: () => void;
  }>;
}

export function MultiSelectFilter({
  label,
  allLabel,
  options,
  selectedIds,
  onChange,
  emptyLabel,
  headerOptions,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query]);

  const summary =
    selectedIds.length === 0
      ? allLabel
      : selectedIds.length === 1
        ? (options.find((option) => option.id === selectedIds[0])?.label ?? allLabel)
        : `${selectedIds.length}`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cn(
          TOOLBAR_CONTROL,
          'inline-flex min-w-[9rem] items-center justify-between gap-2',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-left">
          <span className="mr-1 text-ds-muted">{label}</span>
          {summary}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ds-muted" />
      </button>
      {open && (
        <div className="absolute z-30 mt-s1 w-72 rounded-pop border border-ds-line bg-ds-card p-s2 shadow-pop">
          <div className="flex items-center gap-2 px-1 pb-2">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={allLabel}
              className={cn(TOOLBAR_CONTROL, 'w-full font-normal')}
            />
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-ds-muted hover:text-ds-text"
                aria-label={allLabel}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {headerOptions && headerOptions.length > 0 ? (
            headerOptions.map((option) => (
              <label
                key={option.id}
                className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 ${TYPE.body} hover:bg-ds-row-hover`}
              >
                <input
                  type="checkbox"
                  checked={option.active}
                  onChange={() => option.onSelect()}
                  className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                />
                {option.label}
              </label>
            ))
          ) : (
            <label
              className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 ${TYPE.body} hover:bg-ds-row-hover`}
            >
              <input
                type="checkbox"
                checked={selectedIds.length === 0}
                onChange={() => onChange([])}
                className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
              />
              {allLabel}
            </label>
          )}
          <div className="max-h-56 overflow-auto">
            {filtered.length === 0 ? (
              <p className={`px-2 py-3 ${UI_MUTED}`}>{emptyLabel}</p>
            ) : (
              filtered.map((option) => {
                const checked = selectedIds.includes(option.id);
                return (
                  <label
                    key={option.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 ${TYPE.body} hover:bg-ds-row-hover`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        onChange(
                          checked
                            ? selectedIds.filter((id) => id !== option.id)
                            : [...selectedIds, option.id],
                        );
                      }}
                      className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                    />
                    <span className="truncate">{option.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
